import { Checkbox, Dropdown, Input, Option, TableRow, TextArea } from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { Dispatch, SetStateAction } from 'react';
import { NavigateFunction } from 'react-router-dom';
import {
  addOrRemove,
  convertNumberOfMinutesToTime,
  Customer,
  CustomerArticle,
  CustomerUser,
  generateDropdownOptions,
  Offer,
  Order,
  ProjectCheckResult,
  ProjectProtocol,
  ProjectTimeBooking,
  ProtocolDocumentConfig,
  removeIdSuffix,
  SchambeckUser,
  SimpleUser,
} from 'shared';
import { CustomerLocation, ShiftType } from 'shared/src/customerLocation/CustomerLocation.types';
import {
  AppointmentState,
  ErrorPattern,
  Project,
  ProjectState,
  ProtocolMailingSettings,
  Schedule,
  ScheduleAppointmentDetail,
  ScheduleType,
  WorkInstruction,
} from 'shared/src/project/Project.types';
import dayjs from 'shared/src/tools/Dayjs';
import { uid } from 'uid';
import i18n from '../../i18n/i18n';
import { generateEmptyCustomerArticle } from '../CustomerArticle.utils';
import { getUserNameForSimpleUser } from '../user/User.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

/**
 * Util method to convert projects into tableEntries
 * @param projects Array that contains the projects to display
 * @param customers Array of customers to display the names in the tableRows
 * @returns Array of tableRows
 */
export const convertProjectsIntoTableEntries = (
  projects: Project[],
  customers: Customer[],
  customerLocations: CustomerLocation[],
  customerUsers: CustomerUser[],
  orders: Order[],
  navigate: NavigateFunction,
  handleDownload: (projectId: string) => void,
  handleCopy: (projectId: string) => void,
): TableRow[] => {
  return projects.map((project) => {
    const order: Order | undefined = orders.find((order) => order.id === project.orderId);
    const location: string =
      customerLocations.find((location) => location.id === order?.locationId)?.name || '-';

    const locationContact: CustomerUser | undefined = customerUsers.find(
      (contact) => contact.id === order?.locationContactId,
    );

    const workingLocation: CustomerLocation | undefined = customerLocations.find(
      (location) => location.id === order?.workingLocation.customerLocationId,
    );

    const workLocationCustomer: string =
      customers.find((customer) => customer.id === order?.workingLocation.customerId)?.name || '-';

    const workLocationContact: CustomerUser | undefined = customerUsers.find(
      (contact) => contact.id === order?.workingLocation.customerContactId,
    );
    const invoiceRecipient: string =
      customers.find((customer) => customer.id === order?.invoiceRecipient.customerId)?.name || '-';
    const invoiceRecipientContact: CustomerUser | undefined = customerUsers.find(
      (customerUser) => customerUser.id === order?.invoiceRecipient.customerId,
    );
    const customer: string =
      customers.find((customer) => customer.id === order?.customerId)?.name || '-';

    return {
      id: project.id,
      onClick: () => navigate('/project/edit', { state: { project } }),
      content: [
        project.numberRangeNumber,
        order?.customerOrderNumber || '-',
        ` ${project.startDate?.toLocaleDateString('DE-de')} -
        ${project.endDate?.toLocaleDateString('DE-de') ?? 'open'}`,
        customer,
        location,
        (locationContact?.firstName || '-') + ' ' + (locationContact?.lastName || '-'),
        workLocationCustomer,
        workingLocation?.name || '-',
        (workLocationContact?.firstName || '-') + ' ' + (workLocationContact?.lastName || '-'),
        invoiceRecipient,
        invoiceRecipientContact?.firstName || '-' + ' ' + invoiceRecipientContact?.lastName || '-',
        order?.customerArticleNumber || '-',
        order?.customerArticleDescription || '-',
        project.annotation,
        project.projectState === ProjectState.PROJECT_COMPLETED
          ? i18n.t('pages.project.overview.completed')
          : i18n.t('pages.project.overview.notCompleted'),
        <div className="table-action__project-icon-wrapper">
          <IconComponent
            type={IconType.EDIT}
            className="table-action__project-icon-wrapper__icon"
            onClick={(evt) => {
              evt.stopPropagation();
              navigate('/project/edit', { state: { project } });
            }}
          />
          <IconComponent
            type={IconType.PROTOCOL}
            className="table-action__project-icon-wrapper__icon"
            onClick={(evt) => {
              evt.stopPropagation();
              navigate('/project/protocol', { state: { project } });
            }}
          />
          <IconComponent
            type={IconType.CAR}
            className="table-action__project-icon-wrapper__icon"
            onClick={(evt) => {
              evt.stopPropagation();
              navigate('/delivery/edit', { state: { project } });
            }}
          />
          <IconComponent
            type={IconType.DESCRIPTION}
            className="table-action__project-icon-wrapper__icon description"
            onClick={(evt) => {
              evt.stopPropagation();
              handleDownload(project.id);
            }}
          />
          <IconComponent
            type={IconType.COPY}
            className="table-action__project-icon-wrapper__icon description"
            onClick={(evt) => {
              evt.stopPropagation();
              handleCopy(project.id);
            }}
          />
        </div>,
      ],
    };
  });
};

/**
 * Util method to convert projects into tableEntries
 * @param projects Array that contains the projects to display
 * @param customers Array of customers to display the names in the tableRows
 * @returns Array of tableRows
 */
export const convertProjectsIntoTableEntriesForProtocols = (
  projects: Project[],
  customers: Customer[],
  customerLocations: CustomerLocation[],
  customerUsers: CustomerUser[],
  orders: Order[],
  setSelectedProjects: Dispatch<SetStateAction<string[]>>,
  selectedProjects: string[],
): TableRow[] => {
  return projects.map((project) => {
    const order: Order | undefined = orders.find((order) => order.id === project.orderId);
    const location: string =
      customerLocations.find((location) => location.id === order?.locationId)?.name || '-';

    const locationContact: CustomerUser | undefined = customerUsers.find(
      (contact) => contact.id === order?.locationContactId,
    );

    const workingLocation: CustomerLocation | undefined = customerLocations.find(
      (location) => location.id === order?.workingLocation.customerLocationId,
    );

    const workLocationCustomer: string =
      customers.find((customer) => customer.id === order?.workingLocation.customerId)?.name || '-';

    const workLocationContact: CustomerUser | undefined = customerUsers.find(
      (contact) => contact.id === order?.workingLocation.customerContactId,
    );
    const invoiceRecipient: string =
      customers.find((customer) => customer.id === order?.invoiceRecipient.customerId)?.name || '-';
    const invoiceRecipientContact: CustomerUser | undefined = customerUsers.find(
      (customerUser) => customerUser.id === order?.invoiceRecipient.customerId,
    );
    const customer: string =
      customers.find((customer) => customer.id === order?.customerId)?.name || '-';
    return {
      id: project.id,
      content: [
        <Checkbox
          onCheck={() => setSelectedProjects((prev) => addOrRemove(prev, project.id))}
          isChecked={selectedProjects.includes(project.id)}
        />,
        project.numberRangeNumber,
        order?.customerOrderNumber || '-',
        ` ${project.startDate?.toLocaleDateString('DE-de')} -
        ${project.endDate?.toLocaleDateString('DE-de') ?? 'open'}`,
        customer,
        location,
        (locationContact?.firstName || '-') + ' ' + (locationContact?.lastName || '-'),
        workLocationCustomer,
        workingLocation?.name || '-',
        (workLocationContact?.firstName || '-') + ' ' + (workLocationContact?.lastName || '-'),
        invoiceRecipient,
        invoiceRecipientContact?.firstName || '-' + ' ' + invoiceRecipientContact?.lastName || '-',
        order?.customerArticleNumber || '-',
        order?.customerArticleDescription || '-',
        project.annotation,
        project.projectState === ProjectState.PROJECT_COMPLETED
          ? i18n.t('pages.project.overview.completed')
          : i18n.t('pages.project.overview.notCompleted'),
        order?.lastProtocolDate?.toLocaleDateString('DE-de') || '-',
        order?.protocolSendDates && order.protocolSendDates.length > 0
          ? order.protocolSendDates
              ?.sort((a, b) => (dayjs(a).isBefore(b) ? 1 : -1))[0]
              ?.toLocaleDateString('DE-de')
          : '-',
      ],
    };
  });
};

/**
 * Util method to convert workInstructions into tableRows
 * @param navigate NavigateFunction
 * @param order the order for the project/offer
 * @param project defined if instrctions are acessed through project
 * @param offer defined if instructions are accessed for customerProject
 * @returns Array of TableRows
 */
export const convertWorkInstructionsIntoTableRows = (
  navigate: NavigateFunction,
  order: Order,
  project?: Project,
  offer?: Offer,
): TableRow[] => {
  const instructions: WorkInstruction[] = [];
  if (project?.instructions) instructions.push(...project.instructions);
  else if (offer?.workInstructions) instructions.push(...offer.workInstructions);
  return instructions
    .sort((a, b) => a.index - b.index)
    .map((instruction) => ({
      id: instruction.id,
      onClick: () =>
        navigate('/project/instruction', {
          state: { project, instruction, order, offer },
        }),
      content: [
        instruction.disabled
          ? i18n.t('pages.project.edit.deactivated')
          : i18n.t('pages.project.edit.active'),
        instruction.instructionTimeStamp?.toLocaleDateString('DE-de') || '-',
        instruction.instructionBy || '-',
        instruction.instructionFrom || '-',
      ],
    }));
};

/**
 * Util method to convert errorPatterns into TableRows
 * @param patterns errorPatterns to display
 * @param setProject Dispatch acion to update the project
 * @param disabled boolean if the content should be editable or not
 * @returns Array of TableRows
 */
export const convertErrorPatternsIntoTableRows = (
  patterns: ErrorPattern[],
  setProject: Dispatch<SetStateAction<Project | undefined>> = () => {},
  disabled?: boolean,
): TableRow[] => {
  const letters: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return patterns.map((pattern, index) => ({
    id: pattern.id,
    content: [
      letters[index],
      <div className="project-edit__error-patterns__icon-wrapper">
        <Input
          value={pattern.content}
          type="text"
          disabled={disabled}
          onChange={(content) =>
            setProject(
              (prevValue) =>
                prevValue && {
                  ...prevValue,
                  errorPatterns: prevValue.errorPatterns.map((existingpattern) =>
                    existingpattern.id === pattern.id ? { ...pattern, content } : existingpattern,
                  ),
                },
            )
          }
        />
        {setProject && !disabled && (
          <IconComponent
            type={IconType.DELETE}
            className="project-edit__error-patterns__icon-wrapper__delete-icon"
            onClick={() =>
              setProject(
                (prevValue) =>
                  prevValue && {
                    ...prevValue,
                    errorPatterns: prevValue.errorPatterns.filter(
                      (existingpattern) => existingpattern.id !== pattern.id,
                    ),
                  },
              )
            }
          />
        )}
      </div>,
    ],
  }));
};
/**
 * Util method to generate a new workInstruction
 * @param override setter to adjust the created object
 * @returns generated WorkInstruction
 */
export const generateNewWorkInstruction = (
  override?: Partial<WorkInstruction>,
): WorkInstruction => ({
  content: '',
  createDate: new Date(),
  createdBy: '',
  disabled: false,
  id: uid(),
  index: 0,
  projectId: '',
  instructionTimeStamp: new Date(),
  instructionBy: '',
  instructionFrom: '',
  ...override,
});
/**
 * Util method to generate a new errorPattern
 * @param override setter to adjust the created object
 * @returns generated ErrorPattern
 */
export const generateNewErrorPattern = (override?: Partial<ErrorPattern>): ErrorPattern => ({
  content: '',
  createDate: new Date(),
  createdBy: '',
  disabled: false,
  id: uid(),
  index: 0,
  projectId: '',
  ...override,
});

/**
 * Util method to generate dropdown options for shiftType
 * @returns array of dropdown options
 */
export const generateDropdownOptionsForShift = (shiftsToDisplay?: ShiftType[]): Option[] =>
  Object.values(ShiftType)
    .filter((type) => !shiftsToDisplay || shiftsToDisplay.includes(type))
    .map((shiftType) => ({
      label: i18n.t(`general.shiftType.${shiftType}`),
      value: shiftType,
    }));

/**
 * Util method to generate an empty projectProtocol
 * @param override partial of projectProtocol to adjust generated object
 * @returns generated projectProtocol
 */
export const generateEmptyProjectProtocol = (
  override?: Partial<ProjectProtocol>,
): ProjectProtocol => ({
  checkResults: [],
  createDate: new Date(),
  createdBy: '',
  customerId: '',
  id: '',
  orderId: '',
  projectId: '',
  protocolDate: new Date(),
  shift: ShiftType.EARLY,
  timeBookings: [],
  officeId: '',
  ...override,
});

/**
 * Util method to generate an empty projectCheckResult
 * @param override partial of projectCheckResult to adjust generated object
 * @returns generated projectCheckResult
 */
export const generateEmptyProjectCheckResult = (
  override?: Partial<ProjectCheckResult>,
): ProjectCheckResult => ({
  articleNumber: '',
  checkedTotal: 0,
  foundErrorPatterns: new Map<string, number>(),
  id: uid(),
  io: 0,
  nio: 0,
  note: '',
  rework: 0,
  userIds: [],
  batchNumber: '',
  totalIoAfterRework: 0,
  ...override,
});

/**
 * Util method to convert timeBookoings into tableRows
 * @param timeBookings Array of all timeBookings to display
 * @param users Array of SimpleUsers
 * @param updateTimeBookings method to update the content of the fields onChange
 * @param addUserEntry method to create an additional entry for ann user
 * @returns Array of TableRows
 */
export const convertProjectTimeBookingsIntoTableEntries = (
  timeBookings: ProjectTimeBooking[],
  users: SimpleUser[],
  updateTimeBookings: (updatedBooking: ProjectTimeBooking, remove?: boolean) => void,
  copyTimes: (booking: ProjectTimeBooking) => void,
  addUserEntry: (userId: string) => void,
): TableRow[] => {
  return timeBookings.map((booking) => ({
    id: booking.id,
    content: [
      getUserNameForSimpleUser(removeIdSuffix(booking.userId), users),
      {
        value: (
          <Input
            value={booking.startTime}
            type="time"
            onChangeTime={(startTime) => updateTimeBookings({ ...booking, startTime })}
          />
        ),
        filterValue: convertNumberOfMinutesToTime(booking.startTime) || '00:00',
      },
      {
        value: (
          <Input
            value={booking.endTime}
            type="time"
            onChangeTime={(endTime) => updateTimeBookings({ ...booking, endTime })}
          />
        ),
        filterValue: convertNumberOfMinutesToTime(booking.endTime) || '',
      },
      {
        value: (
          <Input
            value={booking.breakTime}
            type="number"
            onChangeNumber={(breakTime) => updateTimeBookings({ ...booking, breakTime })}
          />
        ),
        filterValue: booking.breakTime,
      },
      {
        value: (
          <Input
            value={booking.customerDrivingTimeArrival}
            type="number"
            onChangeNumber={(customerDrivingTimeArrival) =>
              updateTimeBookings({ ...booking, customerDrivingTimeArrival })
            }
          />
        ),
        filterValue: booking.customerDrivingTimeArrival,
      },
      {
        value: (
          <Input
            value={booking.customerDrivingTimeDeparture}
            type="number"
            onChangeNumber={(customerDrivingTimeDeparture) =>
              updateTimeBookings({ ...booking, customerDrivingTimeDeparture })
            }
          />
        ),
        filterValue: booking.customerDrivingTimeDeparture,
      },

      <Checkbox
        isChecked={booking.customerArrival}
        onCheck={(customerArrival) => updateTimeBookings({ ...booking, customerArrival })}
      />,
      <Checkbox
        isChecked={booking.customerDeparture}
        onCheck={(customerDeparture) => updateTimeBookings({ ...booking, customerDeparture })}
      />,
      {
        value: (
          <Input
            value={booking.userDrivingTimeArrival}
            type="number"
            onChangeNumber={(userDrivingTimeArrival) =>
              updateTimeBookings({ ...booking, userDrivingTimeArrival })
            }
          />
        ),
        filterValue: booking.userDrivingTimeArrival,
      },
      {
        value: (
          <Input
            value={booking.userDrivingTimeDeparture}
            type="number"
            onChangeNumber={(userDrivingTimeDeparture) =>
              updateTimeBookings({ ...booking, userDrivingTimeDeparture })
            }
          />
        ),
        filterValue: booking.userDrivingTimeDeparture,
      },
      {
        value: (
          <Input
            value={booking.breakTimeUser}
            type="number"
            onChangeNumber={(breakTimeUser) => updateTimeBookings({ ...booking, breakTimeUser })}
          />
        ),
        filterValue: booking.breakTimeUser,
      },
      <div className="table-action__icon-wrapper">
        <IconComponent
          type={IconType.ADD}
          onClick={() => addUserEntry(removeIdSuffix(booking.userId))}
        />
        <IconComponent
          type={IconType.ARROW_RANGE}
          className="table-action__icon-wrapper__range"
          onClick={(evt) => {
            evt.stopPropagation();
            copyTimes(booking);
          }}
        />
        <IconComponent
          type={IconType.DELETE}
          className="delete"
          onClick={(evt) => {
            evt.stopPropagation();
            updateTimeBookings(booking, true);
          }}
        />
      </div>,
    ],
  }));
};

/**
 * Util method to convert CheckResults To TableEntries
 * @param results ProjectCheckResults to convert
 * @param updateCheckResults method to update the results onChange
 * @param errorPatterns array of relevant errorPatterns for the project
 * @returns Array of TableRows
 */
export const convertCheckResultsToTableEntries = (
  results: ProjectCheckResult[],
  updateCheckResults: (updatedResult: ProjectCheckResult, deleteEntry?: boolean) => void,
  errorPatterns: ErrorPattern[],
  customerArticles: CustomerArticle[],
  checkPlausibility: (id: string) => void,
  plannedUsers: SchambeckUser[],
): TableRow[] => {
  const availableArticles: CustomerArticle[] = [...customerArticles];
  results.forEach((result) => {
    if (customerArticles.some((article) => article.partnumber === result.articleNumber)) return;
    else if (
      result.articleNumber !== '' &&
      !availableArticles.some((art) => art.partnumber === result.articleNumber)
    )
      availableArticles.push(generateEmptyCustomerArticle({ partnumber: result.articleNumber }));
  });
  return results.map((result) => ({
    id: result.id,
    content: [
      {
        value: (
          <Dropdown
            minWidth="150px"
            selectedOption={result.articleNumber}
            onCreateNew={(value: Option) => {
              checkPlausibility(result.id);
              updateCheckResults({ ...result, articleNumber: value.value });
            }}
            options={generateDropdownOptions(availableArticles, 'partnumber', 'partnumber')}
            create
            onChange={(articleNumber) => updateCheckResults({ ...result, articleNumber })}
          />
        ),
        filterValue:
          availableArticles.find((article) => article.partnumber === result.articleNumber)
            ?.partnumber || '',
      },
      {
        value: (
          <Dropdown
            options={generateDropdownOptions(plannedUsers, ['lastName', 'firstName'], 'id')}
            onChangeMultiple={(selectedUsers) =>
              updateCheckResults({ ...result, userIds: selectedUsers })
            }
            multi
            selectedMultiOptions={result.userIds}
          />
        ),
        filterValue:
          plannedUsers
            .filter((user) => result.userIds.includes(user.id))
            .map((user) => user.firstName + user.lastName)
            .join(' ') || '',
      },
      {
        value: (
          <TextArea
            resizable
            onChange={(batchNumber) => updateCheckResults({ ...result, batchNumber })}
            value={result.batchNumber}
          />
        ),
        filterValue: result.batchNumber || '',
      },
      {
        value: (
          <TextArea
            resizable
            onChange={(note) => updateCheckResults({ ...result, note })}
            value={result.note}
          />
        ),
        filterValue: result.note,
      },
      {
        value: (
          <Input
            type="number"
            onChangeNumber={(checkedTotal) => {
              checkPlausibility(result.id);
              updateCheckResults({ ...result, checkedTotal });
            }}
            value={result.checkedTotal}
            hideArrows
          />
        ),
        filterValue: result.checkedTotal,
      },
      {
        value: (
          <Input
            hideArrows
            type="number"
            onChangeNumber={(io) => {
              checkPlausibility(result.id);
              updateCheckResults({ ...result, io });
            }}
            value={result.io}
          />
        ),
        filterValue: result.io,
      },
      {
        value: (
          <Input
            hideArrows
            type="number"
            onChangeNumber={(rework) => {
              checkPlausibility(result.id);
              updateCheckResults({ ...result, rework });
            }}
            value={result.rework}
          />
        ),
        filterValue: result.rework,
      },
      {
        value: (
          <Input
            hideArrows
            type="number"
            onChangeNumber={(totalIoAfterRework) => {
              checkPlausibility(result.id);
              updateCheckResults({ ...result, totalIoAfterRework });
            }}
            value={result.totalIoAfterRework}
          />
        ),
        filterValue: result.totalIoAfterRework,
      },

      ...errorPatterns.map((pattern) => ({
        value: (
          <Input
            hideArrows
            value={result.foundErrorPatterns.get(pattern.id) || 0}
            type="number"
            onChangeNumber={(newValue) => {
              checkPlausibility(result.id);
              const existingPatterns: Map<string, number> = result.foundErrorPatterns;
              existingPatterns.set(pattern.id, newValue);
              updateCheckResults({
                ...result,
                foundErrorPatterns: existingPatterns,
              });
            }}
          />
        ),
        filterValue: result.foundErrorPatterns.get(pattern.id) || '',
      })),
      <div className="table-action__icon-wrapper">
        <IconComponent
          type={IconType.DELETE}
          className="delete"
          onClick={() => updateCheckResults(result, true)}
        />
      </div>,
    ],
  }));
};

/**
 * TODO - TEST ME
 * Util method to generate TableHeaders for the checkResults with default headers and additional headers based on the ErrorPatterns
 * @param errorPatterns array of errorpatterns to include
 * @returns Array of tableHeaders
 */
export const generateErrorPatternTableHeaders = (errorPatterns: ErrorPattern[]): TableHeader[] => {
  const letters: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const defaultHeaders: TableHeader[] = i18n.t('pages.project.protocol.resultHeaders', {
    returnObjects: true,
  }) as TableHeader[];
  const actionHeader: TableHeader = defaultHeaders[defaultHeaders.length - 1];
  defaultHeaders.pop();
  const patternHeaders: TableHeader[] = errorPatterns.map(
    (pattern) =>
      ({
        text: letters[pattern.index],
        visible: true,
        growFactor: 0.3,
        showSum: true,
      }) as TableHeader,
  );
  return [...defaultHeaders, ...patternHeaders, actionHeader];
};

/**
 * Util method to generate an empty projectTimeBooking
 * @param override partial timeBooking to adjust the generated object
 * @returns  generated timeBooking
 */
export const generateEmptyProjectTimeBooking = (
  override: Partial<ProjectTimeBooking>,
): ProjectTimeBooking => ({
  id: uid(),
  userId: '',
  startTime: 0,
  endTime: 0,
  userDrivingTimeDeparture: 0,
  userDrivingTimeArrival: 0,
  customerDrivingTimeArrival: 0,
  customerDrivingTimeDeparture: 0,
  breakTime: 0,
  breakTimeUser: 0,
  note: '',
  customerArrival: false,
  customerDeparture: false,
  scheduled: false,
  ...override,
});

/**
 * Util method to check if the data in a projectCheckResult is plausible or not
 * @param result to check
 * @returns boolean value if plausible
 */
/* eslint-disable no-fallthrough */
export const isResultPlausible = (result: ProjectCheckResult): boolean => {
  const errorAmount: number = result.foundErrorPatterns
    ? Array.from(result.foundErrorPatterns.values()).reduce((a, b) => a + b, 0)
    : 0;
  const resultOverlimit: boolean = Array.from(result.foundErrorPatterns.values()).some(
    (resultEntry) => resultEntry > result.checkedTotal - result.io,
  );
  switch (true) {
    // no article number at all
    case !result.articleNumber:
    // total iO is smaller than iO
    case result.totalIoAfterRework < result.io:
    // total iO is larger than total checked amount
    case result.totalIoAfterRework > result.checkedTotal:
    // more iO after rework than iO and rework combined
    case result.totalIoAfterRework > result.rework + result.io:
    // less parts checked in total than rework
    case result.checkedTotal < result.rework:
    // more rework than actual errors
    case result.rework > errorAmount:
    // single ErrorPattern has more found error than totalChecked - io
    case resultOverlimit:
      return false;
    default:
      return true;
  }
};

/**
 * Util method to create empty project setttings
 * @param override partial ProtocolMailingSettings
 * @returns created object
 */
export const generateEmptyProtocolMailingSettings = (
  override?: Partial<ProtocolMailingSettings>,
): ProtocolMailingSettings => ({
  setting: ProtocolDocumentConfig.FULL,
  receivers: [],
  ...override,
});

/**
 * Util method to create an empty projectSchedule
 * @param override partial ProjectSchedule to adjust the generated object
 * @returns created project schedule
 */
export const generateEmptySchedule = (override?: Partial<Schedule>): Schedule => ({
  id: undefined!,
  orderId: '',
  referenceId: '',
  scheduleDate: undefined!,
  scheduledCars: new Map<ShiftType, string[]>(),
  scheduledUsers: new Map<ShiftType, string[]>(),
  type: ScheduleType.PROJECT_SCHEDULE,
  ...override,
});

/**
 * Helper to create a new instance of ScheduleAppointmentDetail. Keep in mind that this
 * object is not living on itself but needs a Schedule to live in
 *
 * @param override Optional override parameter
 * @returns A new ScheduleAppointmentDetail
 */
export const generateEmptyAppointmentDetails = (
  override?: Partial<ScheduleAppointmentDetail>,
): ScheduleAppointmentDetail => ({
  id: uid(),
  title: '',
  description: '',
  location: '',
  reasonId: '',
  startTime: 0,
  endTime: 0,
  entireDay: false,
  state: AppointmentState.ABSENT,
  ...override,
});

/**
 * Util method to convert protocols into TableEntries
 * @param protocols to convert
 * @param customers to show in the table
 * @param users to show in the table
 * @param selectedProtocols list of all selected Protocols
 * @param setSelectedProtocols update method for the selectedProtocols
 * @returns  array of generated TableRows
 */
export const convertProtocolsIntoTableEntries = (
  protocols: ProjectProtocol[],
  customers: Customer[],
  users: SimpleUser[],
  selectedProtocols: string[],
  setSelectedProtocols: Dispatch<SetStateAction<string[]>>,
): TableRow[] =>
  protocols.map((protocol) => {
    const customer: Customer | undefined = customers.find(
      (customer) => customer.id === protocol.customerId,
    );
    return {
      id: protocol.id,
      content: [
        <Checkbox
          isChecked={selectedProtocols.includes(protocol.id)}
          onCheck={() =>
            setSelectedProtocols(
              selectedProtocols.includes(protocol.id)
                ? selectedProtocols.filter((id) => id !== protocol.id)
                : [...selectedProtocols, protocol.id],
            )
          }
        />,
        protocol.protocolDate.toLocaleDateString('de-DE'),
        customer?.name || '-',
        getUserNameForSimpleUser(protocol.createdBy, users),
      ],
    };
  });

/**
 * Util method to convert projects into tableEntries
 * @param projects to convert
 * @param navigate naviagteFunction
 * @returns  Array of TableRows
 */
export const convertRelatedProjectsIntoTableEntries = (
  projects: Project[],
  navigate: NavigateFunction,
): TableRow[] => {
  return projects.map((project) => ({
    id: project.id,
    content: [project.numberRangeNumber, project.startDate?.toLocaleDateString('DE-de')],
    onClick: () => navigate('/project/edit', { state: { project } }),
  }));
};

/**
 * Helper method to get the next shift in an ascending or descending order
 * @param currentShift to get the next one for
 * @param availableShifts all shifts that are available
 * @param next boolean if the next shift should be given ascending or descending
 * @returns the next shiftType
 */
export const getNextShift = (
  currentShift: ShiftType,
  availableShifts: ShiftType[],
  next: boolean = false,
): ShiftType => {
  //The order is important, becasue it is used to get the next shift
  const shifts: ShiftType[] = [ShiftType.EARLY, ShiftType.LATE, ShiftType.NIGHT, ShiftType.NORMAL];
  const currentIndex: number = shifts.findIndex((shift) => currentShift === shift);
  if (next) {
    const indexToLoad: number = currentIndex === availableShifts.length - 1 ? 0 : currentIndex + 1;
    return shifts.filter((shift) => availableShifts.includes(shift))[indexToLoad];
  } else {
    const indexToLoad: number = currentIndex === 0 ? availableShifts.length - 1 : currentIndex - 1;
    return shifts.filter((shift) => availableShifts.includes(shift))[indexToLoad];
  }
};

/**
 * Util method to check if a shift is earlier or later compared to another one
 * @param oldShift to check
 * @param newShift  to check
 * @returns boolean if the type is earlier or not
 */
export const shiftIsEarlier = (oldShift: ShiftType, newShift: ShiftType): boolean => {
  switch (newShift) {
    case ShiftType.EARLY:
      return true;
    case ShiftType.LATE:
      return oldShift === ShiftType.NIGHT || oldShift === ShiftType.NORMAL;
    case ShiftType.NIGHT:
      return oldShift === ShiftType.NORMAL;
    case ShiftType.NORMAL:
      return false;
  }
};
